import { useEffect } from "react";
import { AlertCircle, ArrowLeft, X } from "lucide-react";
import { Button } from "@/components/ui/Button";

export type ValidationIssue = {
  field: string; // nome campo user-friendly (es. "Orario inizio di 'Misto'")
  step?: number; // step del wizard dove sta il campo
  hint?: string; // suggerimento opzionale
};

export function ValidationDialog({
  open,
  issues,
  onClose,
  onReviewAt,
  onProceed,
  title = "Alcuni campi non sono compilati",
  loading,
}: {
  open: boolean;
  issues: ValidationIssue[];
  onClose: () => void;
  /** Naviga allo step indicato del wizard (chiude popup) */
  onReviewAt: (step: number) => void;
  /** Procede col submit ignorando i vuoti */
  onProceed: () => void;
  title?: string;
  loading?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const multiple = issues.length > 1;
  const firstStep = issues.find((i) => typeof i.step === "number")?.step;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <p className="mt-1 text-sm text-slate-600">
              {multiple
                ? `Hai lasciato ${issues.length} campi vuoti. È voluto, o vuoi rivederli?`
                : "Hai lasciato un campo vuoto. È voluto, o vuoi rivederlo?"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Lista campi */}
        <ul className="max-h-60 divide-y divide-slate-100 overflow-y-auto">
          {issues.map((it, i) => (
            <li
              key={i}
              className="flex items-start gap-3 px-5 py-2.5 text-sm"
            >
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-700">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900">{it.field}</div>
                {it.hint ? (
                  <div className="text-xs text-slate-500">{it.hint}</div>
                ) : null}
              </div>
              {typeof it.step === "number" ? (
                <button
                  onClick={() => onReviewAt(it.step!)}
                  className="text-xs font-medium text-court-600 hover:text-court-800"
                >
                  Step {it.step} →
                </button>
              ) : null}
            </li>
          ))}
        </ul>

        {/* Actions */}
        <div className="flex flex-col gap-2 border-t border-slate-100 px-5 py-3 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={() => {
              if (typeof firstStep === "number") onReviewAt(firstStep);
              else onClose();
            }}
            disabled={loading}
          >
            <ArrowLeft className="h-4 w-4" />
            Torna a compilare
          </Button>
          <Button
            variant="primary"
            onClick={onProceed}
            loading={loading}
          >
            Crea comunque
          </Button>
        </div>
      </div>
    </div>
  );
}
